import { useState, useRef, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  Alert,
  Stack,
  FormHelperText,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import ReadOnlyTooltip from "../../../components/Reusable/ReadOnlyTooltip";
import { useViewAs } from "../../../viewAs";

export default function FileUploadField({
  value = null,
  onChange,
  error = false,
  helperText = "",
  label = "Upload File",
  accept = "image/*,application/pdf",
  maxSizeMB = 25,
}) {
  const [uploadState, setUploadState] = useState(["init", null]);
  const [progress, setProgress] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fileBlob, setFileBlob] = useState(null);
  const [fileContentType, setFileContentType] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const { api } = useOutletContext();
  const { readOnly } = useViewAs();

  const [status, data] = uploadState;

  useEffect(() => {
    return () => {
      if (fileBlob) {
        URL.revokeObjectURL(fileBlob);
      }
    };
  }, [fileBlob]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setUploadState([
        "error",
        { message: `File exceeds ${maxSizeMB}MB limit` },
      ]);
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64Data = e.target.result;

      setUploadState(["uploading", { filename: file.name }]);
      setProgress(0);

      try {
        const result = await api.uploadAttachment(
          base64Data,
          file.name,
          (percent) => setProgress(percent)
        );

        setUploadState(["loaded", result]);
        onChange(result.attachmentId);
      } catch (err) {
        setUploadState(["error", { message: err.message }]);
      }
    };

    reader.onerror = () => {
      setUploadState(["error", { message: "Failed to read file" }]);
    };

    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleView = async () => {
    if (!value) return;

    setLoadingFile(true);
    try {
      const { blob, contentType } = await api.viewAttachment(value);
      const blobUrl = URL.createObjectURL(blob);
      setFileBlob(blobUrl);
      setFileContentType(contentType);
      setDialogOpen(true);
    } catch (err) {
      setUploadState(["error", { message: err.message }]);
    } finally {
      setLoadingFile(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (fileBlob) {
      URL.revokeObjectURL(fileBlob);
      setFileBlob(null);
      setFileContentType(null);
    }
  };

  const handleClear = async () => {
    if (!value) return;

    try {
      await api.deleteAttachment(value);
      setUploadState(["init", null]);
      onChange(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      setUploadState(["error", { message: err.message }]);
    }
  };

  const handleRetry = () => {
    setUploadState(["init", null]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box>
      <input
        type="file"
        accept={accept}
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {status === "init" && (
        <ReadOnlyTooltip block>
          <Button
            variant="outlined"
            size="large"
            onClick={handleUploadClick}
            fullWidth
            disabled={readOnly}
            startIcon={<UploadFileIcon />}
            sx={{
              minHeight: 44,
              borderColor: error ? "error.main" : undefined,
              color: error ? "error.main" : undefined,
            }}
          >
            {label}
          </Button>
        </ReadOnlyTooltip>
      )}

      {status === "uploading" && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <UploadFileIcon color="primary" />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {data?.filename}
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Uploading... {progress}%
          </Typography>
        </Box>
      )}

      {status === "loaded" && (
        <Box
          sx={{
            border: 1,
            borderColor: "success.main",
            borderRadius: 1,
            p: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {data?.filename}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={
                loadingFile ? (
                  <CircularProgress size={16} />
                ) : (
                  <VisibilityIcon />
                )
              }
              onClick={handleView}
              disabled={loadingFile}
              sx={{ minHeight: 44 }}
            >
              {loadingFile ? "Loading..." : "View"}
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleClear}
              color="error"
              sx={{ minHeight: 44 }}
            >
              Clear
            </Button>
          </Stack>
        </Box>
      )}

      {status === "error" && (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {data?.message || "Upload failed"}
          </Alert>
          <Button
            variant="outlined"
            size="large"
            onClick={handleRetry}
            fullWidth
            startIcon={<UploadFileIcon />}
            sx={{ minHeight: 44 }}
          >
            Retry Upload
          </Button>
        </Box>
      )}

      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}

      <Dialog fullScreen open={dialogOpen} onClose={handleCloseDialog}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseDialog}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {data?.filename || "View File"}
            </Typography>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            overflow: "auto",
          }}
        >
          {fileBlob && fileContentType?.startsWith("image/") && (
            <img
              src={fileBlob}
              alt={data?.filename || "Uploaded file"}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}
          {fileBlob && fileContentType === "application/pdf" && (
            <iframe
              src={fileBlob}
              title={data?.filename || "PDF Viewer"}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          )}
          {fileBlob &&
            !fileContentType?.startsWith("image/") &&
            fileContentType !== "application/pdf" && (
              <Box sx={{ textAlign: "center", p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Preview not available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This file type cannot be previewed in the browser.
                </Typography>
              </Box>
            )}
        </Box>
      </Dialog>
    </Box>
  );
}
